import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtMin(mins) {
  if (mins === null || mins === undefined) return "-";
  const m = Math.round(mins);
  if (m === 0) return "-";
  const h = Math.floor(Math.abs(m) / 60);
  const remM = Math.abs(m) % 60;
  const sign = m < 0 ? "-" : "";
  if (h === 0) return `${sign}${remM}m`;
  if (remM === 0) return `${sign}${h}h`;
  return `${sign}${h}h ${remM}m`;
}

export default function PayrollReconciliation() {
  const { token } = useAuth();
  const [date, setDate] = useState(() => {
    // Use LOCAL date, not UTC (toISOString returns UTC and rolls forward late evening)
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/timesheets/admin/reconciliation/${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, token]);

  return (
    <div className="space-y-6 p-6 max-w-7xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/timesheet" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-1" data-testid="recon-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Timesheet
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Payroll Reconciliation</h1>
          <p className="text-sm text-slate-500 mt-1">
            Side-by-side: Time Tracker (Live Status) vs Timesheet (committed). Anything non-zero needs a manager's eye before approval.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
            data-testid="recon-date-picker"
          />
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="recon-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card className="p-4 bg-red-50 border-red-200 text-red-700 text-sm" data-testid="recon-error">
          {error}
        </Card>
      )}

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryStat label="Workers" value={data.summary.total_workers} />
          <SummaryStat
            label="Out of sync"
            value={data.summary.out_of_sync}
            tone={data.summary.out_of_sync > 0 ? "red" : "green"}
          />
          <SummaryStat
            label="Active block open"
            value={data.summary.with_active_block}
            tone={data.summary.with_active_block > 0 ? "amber" : "slate"}
          />
          <SummaryStat
            label="Auto-closed today"
            value={data.summary.with_auto_closed_blocks}
            tone={data.summary.with_auto_closed_blocks > 0 ? "amber" : "slate"}
          />
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-right px-4 py-3">Live</th>
                <th className="text-right px-4 py-3">Timesheet</th>
                <th className="text-right px-4 py-3">Delta</th>
                <th className="text-center px-4 py-3">Manual edits</th>
                <th className="text-center px-4 py-3">Flags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(!data?.rows || data.rows.length === 0) && (
                <tr>
                  <td colSpan={8} className="text-center px-4 py-12 text-slate-400" data-testid="recon-empty">
                    {loading ? "Loading..." : "No worked time on this date."}
                  </td>
                </tr>
              )}
              {data?.rows?.map((row) => (
                <tr key={row.user_id} className={!row.in_sync ? "bg-red-50/40" : row.manual_only ? "bg-blue-50/30" : ""}>
                  <td className="px-4 py-3">
                    {row.in_sync ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" data-testid={`recon-ok-${row.user_id}`} />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-500" data-testid={`recon-fail-${row.user_id}`} />
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{row.user_name}</td>
                  <td className="px-4 py-3 text-slate-600">{row.role}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtMin(row.live_minutes)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtMin(row.timesheet_minutes)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums font-medium ${row.in_sync ? (row.manual_only ? "text-blue-600" : "text-emerald-600") : "text-red-600"}`}>
                    {row.manual_only ? "Manual" : row.in_sync ? "OK" : fmtMin(row.delta_minutes)}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-600">
                    {row.manual_edits || "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {row.active_block_open && (
                        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">open</Badge>
                      )}
                      {row.any_auto_closed && (
                        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">auto-closed</Badge>
                      )}
                      {row.last_update_sources?.includes("time_tracker") && (
                        <Badge variant="outline" className="text-slate-500 border-slate-200">tracker</Badge>
                      )}
                      {row.last_update_sources?.includes("manual") && (
                        <Badge variant="outline" className="text-slate-500 border-slate-200">manual</Badge>
                      )}
                      {row.last_update_sources?.includes("manual_legacy") && (
                        <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">manual (legacy)</Badge>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SummaryStat({ label, value, tone = "slate" }) {
  const palette = {
    slate: "bg-slate-50 text-slate-700",
    green: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
    amber: "bg-amber-50 text-amber-700",
  }[tone];
  return (
    <Card className={`p-4 ${palette}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </Card>
  );
}
